import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Embedding Pliable',
  content: `
You can embed Pliable dashboards, charts, and Ellie chat directly inside your own application using an \`<iframe>\`. Auth is handled server-to-server: your backend mints a short-lived embed session for each end-user and renders an iframe pointing at Pliable. No Pliable user account is required for the people who view the embed.

This guide walks through embedding a **dashboard**. The same flow works for \`chart\` and \`chat\` — just change the \`flow\` and \`target_id\` fields when you create the session.

---

## Prerequisites

- **API credentials.** Generate them from **Manage Users → API Tokens** in the Pliable app — see [Pliable API](/docs/pliable-api). Use the **Builder** role.
- **A dashboard ID.** Open the dashboard in Pliable and grab the ID from the URL: \`https://app.pliable.co/dashboard/<dashboard_id>\`.

---

## Step 1 — Register your embedding origin

Add the origin of the site that will host the iframe to **Settings → Embedded Origins** in the Pliable app. Pliable enforces the list via the CSP \`frame-ancestors\` directive — any origin not in the list will be blocked from loading the iframe.

- Origin = scheme + host + port, e.g. \`https://app.customer.com\`. Paths and trailing slashes are ignored.
- An origin can only be registered to one Pliable tenant at a time.
- Add every origin you'll embed from (production, staging, local dev, etc.).

This is one-time setup per origin. Steps 3–5 below run on every page render.

---

## Step 2 — Set up a custom domain (optional, recommended)

By default, the embed iframe loads from \`app.pliable.co\`. You can serve it from your own domain instead — for example, \`pliable.your-company.com\` — by pointing a subdomain at Pliable. Contact Pliable support to set this up; it's a one-time DNS task.

**Why it matters:** Modern browsers (Safari, Chrome) treat an iframe loaded from a different domain than the page hosting it as a "third-party" page, and won't let it remember the signed-in user across page loads. So:

- **With a custom domain**, the embed is treated as part of your own site. The user can refresh the page or click around inside the embed (drilling into a chart, opening a different dashboard from a list, etc.) and stay signed in for the lifetime of the session.
- **Without a custom domain**, the embed loads correctly the first time, but **reloading the page signs the user out**, and interactions inside the embed that change views may also fail with a sign-out error mid-session. Your application would need to mint a fresh embed URL on each page render and re-create the iframe.

If you're embedding a single, mostly-static dashboard view that the user won't refresh or navigate inside, the no-custom-domain path is fine. For anything more interactive, set up a custom domain.

---

## Step 3 — Authenticate

Exchange your API credentials for a Bearer access token. This is the same call documented at [Auth → API credentials](/api-docs/public#tag/Auth/operation/auth_with_api_creds).

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/authenticate" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_id": "acme_co",
    "client_id": "XXXXX",
    "client_secret": "XXXXX"
  }'
\`\`\`

Response:

\`\`\`json
{
  "access_token": "eyJhbGciOi...",
  "token_type": "Bearer",
  "expires_in": 3600
}
\`\`\`

> **Tip:** The access token is reusable for the full \`expires_in\` window (1 hour by default). **Cache it** — don't call \`/authenticate\` for every embed session you create. The next step uses this same token; you only need to refresh it once it expires.

---

## Step 4 — Create an embed session

Call [Embed → Create embed session](/api-docs/public#tag/Embed/operation/init_embed_session) from your backend, passing the Bearer token from Step 3. This mints a short-lived JWT scoped to a single end-user and returns a one-time URL you embed in an iframe.

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/init-session" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "flow": "dashboard",
    "target_id": "DASHBOARD_ID",
    "sub_id": "your-end-user-id",
    "email": "viewer@customer.example",
    "ttl_seconds": 3600,
    "data_scope": {
      "dimension_filters": {
        "Region": ["US", "Canada"]
      },
      "blacklisted_metrics": ["Internal Margin"]
    }
  }'
\`\`\`

### What goes in the request

| Field | Description |
|---|---|
| \`flow\` | \`dashboard\`, \`chart\`, or \`chat\`. Determines what loads inside the iframe. |
| \`target_id\` | Required for \`dashboard\` and \`chart\` — the object's ID in Pliable. Optional for \`chat\` (omit to start a fresh conversation). |
| \`sub_id\` | An opaque identifier for **your** end-user. Stored on the session for audit purposes. |
| \`email\` | Optional. Display-only; not used for authentication. |
| \`ttl_seconds\` | Lifetime of the embed session, between 300 (5 min) and 3600 (1 hour). Defaults to 3600. |
| \`data_scope.dimension_filters\` | **Required, must be non-empty.** Maps a dimension name (or ObjectId) to the list of values this end-user is allowed to see. Applied as an \`IN\` filter to every analysis query in the session. |
| \`data_scope.blacklisted_metrics\` | Optional list of metric names (or ObjectIds) to hide from the end-user. |

> **Note:** \`dimension_filters\` is mandatory because embed sessions run with the \`ReadOnly_Partner\` role — the data the end-user sees must always be row-scoped to what their organization is allowed to see.

### Response

\`\`\`json
{
  "url": "https://your-custom-domain.com/embed?code=Xa3z...",
  "expires_at": 1738013400,
  "jti": "abcdef1234567890"
}
\`\`\`

- \`url\` — the iframe \`src\`. Contains a one-time code that the iframe exchanges for the JWT on first load.
- \`expires_at\` — Unix timestamp when the JWT expires.
- \`jti\` — JWT ID. Useful for correlating logs.

> **Tip:** The one-time code in the URL is single-use and expires in **60 seconds**. Generate the URL right before you render the iframe, not ahead of time.

---

## Step 5 — Render the iframe

Drop the URL into an \`<iframe>\` on your page:

\`\`\`html
<iframe
  src="https://your-custom-domain.com/embed?code=Xa3z..."
  width="100%"
  height="800"
  frameborder="0"
  allow="clipboard-write">
</iframe>
\`\`\`

That's it. The iframe loads (on your custom domain if you set one up in Step 2, otherwise on \`app.pliable.co\`), exchanges the one-time code for the embed JWT, and renders the dashboard with the row-level filters and metric blacklist you specified in Step 4.

---

## End-to-end example

A typical backend handler — when your end-user opens a page that contains an embedded Pliable dashboard, you generate a fresh embed session URL and pass it to your template.

\`\`\`python
import requests

# Cache this token — reuse until it's near expiry
auth = requests.post(
    "https://app.pliable.co/api/v1/ext/authenticate",
    json={
        "account_id": "acme_co",
        "client_id": PLIABLE_CLIENT_ID,
        "client_secret": PLIABLE_CLIENT_SECRET,
    },
).json()
access_token = auth["access_token"]

# One call per page render — fresh embed URL each time
session = requests.post(
    "https://app.pliable.co/api/v1/ext/init-session",
    headers={"Authorization": f"Bearer {access_token}"},
    json={
        "flow": "dashboard",
        "target_id": "679a86e0362d3d1bd69ee54b",
        "sub_id": current_user.id,
        "email": current_user.email,
        "data_scope": {
            "dimension_filters": {
                "Customer ID": [current_user.customer_id],
            },
        },
    },
).json()

return render_template("dashboard.html", embed_url=session["url"])
\`\`\`

---

## FAQ

**Can I use the same access token to create many embed sessions?**
Yes — that's the recommended pattern. Authenticate once, cache the access token in your backend, and call \`/init-session\` for each embed session you need. Re-authenticate only when the token expires (default 1 hour).

**What happens if the user refreshes the iframe or navigates inside it?**
The embed JWT is persisted as an \`HttpOnly\` cookie scoped to the iframe, so a refresh or in-app navigation reuses the existing session — no new \`/init-session\` call needed until the JWT expires.

**Can the end-user see anything outside what I scoped them to?**
No. \`dimension_filters\` are applied to every analysis query inside the session, including any queries Ellie makes if you're using \`flow: "chat"\`. \`blacklisted_metrics\` hides those metrics from query results entirely. The embed JWT is also pinned to the \`ReadOnly_Partner\` role, which blocks all write endpoints.

**Can I embed Pliable on \`app.pliable.co\` instead of my custom domain?**
Yes. \`init-session\` returns a URL on your custom domain if you've configured one, and falls back to \`app.pliable.co\` otherwise. See Step 2 for what changes without a custom domain.
`,
};

export default doc;
